// src/components/APanel/QuestionEditor/QuestionsPage.jsx
//
// Admin → „Întrebări”: un singur editor pentru biletele de examen (B…) și de teorie
// (P1–P24). Sus rândul comun (.pageToolbar): setul, căutarea (întrebare, bilet sau
// răspuns), limba și meniul „Unelte” (verificarea, exportul, categoriile, importurile;
// importurile se deschid în sub-popup-uri și, după aplicare, setul se reîncarcă).
// Dedesubt, întrebarea găsită arătată ca la elev la examen (numerele întrebărilor,
// textul, imaginea în stânga, răspunsurile în dreapta) și editată pe loc. Modificările
// nesalvate se păstrează pe întrebare cât treci prin bilete.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { getQuestionCategoriesWithCount } from "../../../api/questionCategoriesService";
import { closeSubPopup, openPopup, openSubPopup } from "../../Common/popupUIStore";
import { ButtonUI, CardUI, DropdownUI, InputUI, ListUI, SearchBarUI, StatusUI, TagUI } from "../../UI";
import { cornerClass, joinedCorners, useGridColumns } from "../../UI/joined";
import addIcon from "../../../assets/svg/add.svg";
import ExamImportPopup from "./ExamImportPopup";
import ExamNewTicketPopup from "./ExamNewTicketPopup";
import TheoryImportPopup from "./TheoryImportPopup";
import {
   MAX_ANSWERS,
   MIN_ANSWERS,
   NO_IMAGE_URL,
   SETS,
   buildSearchIndex,
   exportSet,
   imageUrl,
   isDirty,
   loadTickets,
   saveQuestion,
   search,
   verifyQuestion,
} from "./questionBank";

const LANGS = [
   { value: "ro", label: "Română" },
   { value: "ru", label: "Русский" },
];

const cx = (...a) => a.filter(Boolean).join(" ");
const cap = (lang) => (lang === "ru" ? "Ru" : "Ro");

// câmpul de text care crește cu ce scrii (textul întrebării, răspunsurile)
function AutoText({ value, onChange, className, placeholder, ariaLabel }) {
   const ref = useRef(null);
   useLayoutEffect(() => {
      const el = ref.current;
      if (!el) return;
      el.style.height = "0px";
      el.style.height = `${el.scrollHeight}px`;
   }, [value]);
   return (
      <textarea
         ref={ref}
         rows={1}
         className={className}
         value={value}
         placeholder={placeholder}
         aria-label={ariaLabel}
         onChange={(e) => onChange(e.target.value)}
      />
   );
}

// verificarea setului, într-un sub-popup: fiecare problemă duce la întrebarea ei
function VerifyPopup({ setLabel, rows, onOpen }) {
   return (
      <CardUI level="popup" title="Verificare" description={`${setLabel}: ${rows.length ? `${rows.length} întrebări cu probleme` : "nicio problemă"}`}>
         {rows.length ? (
            <ListUI>
               {rows.map((r) => (
                  <ListUI.Item
                     key={r.question.id}
                     title={`${r.ticket.label} · întrebarea ${r.question.order}`}
                     meta={`#${r.question.id}`}
                     detail={r.issues.join(" · ")}
                     onClick={() => {
                        onOpen(r.ticket.id, r.question.id);
                        closeSubPopup();
                     }}
                  />
               ))}
            </ListUI>
         ) : (
            <StatusUI tone="success">Toate întrebările au text, răspunsuri și răspunsul corect.</StatusUI>
         )}
      </CardUI>
   );
}

export default function QuestionsPage() {
   const [set, setSet] = useState("EXAM");
   const [lang, setLang] = useState(() => {
      try {
         return localStorage.getItem("exam.lang") === "ru" ? "ru" : "ro";
      } catch {
         return "ro";
      }
   });
   const [tickets, setTickets] = useState([]);
   const [counts, setCounts] = useState({});
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [ticketId, setTicketId] = useState(null);
   const [questionId, setQuestionId] = useState(null);
   const [drafts, setDrafts] = useState({});
   const [query, setQuery] = useState("");
   const [saving, setSaving] = useState(false);
   const [notice, setNotice] = useState(null);
   const [categories, setCategories] = useState([]);
   const [imgFailed, setImgFailed] = useState(false);
   // numerele întrebărilor: grila își rotunjește singură colțurile de afară
   const [boardCols, boardRef] = useGridColumns(24);

   // ── încărcarea setului ──
   const load = useCallback(async (which) => {
      setLoading(true);
      setError("");
      try {
         const list = await loadTickets(which);
         setTickets(list);
         setCounts((c) => ({ ...c, [which]: list.length }));
         const first = list.find((t) => t.questions.length) || list[0];
         setTicketId(first?.id ?? null);
         setQuestionId(first?.questions[0]?.id ?? null);
      } catch (e) {
         setTickets([]);
         setError(e?.message || "Nu am putut încărca biletele.");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      load(set);
   }, [load, set]);

   // după un import: setul din nou, pe aceeași întrebare (ciornele nesalvate rămân)
   const setRef = useRef(set);
   setRef.current = set;
   const selectionRef = useRef({});
   selectionRef.current = { ticketId, questionId };
   const reload = useCallback(async (which) => {
      if (setRef.current !== which) return;
      try {
         const list = await loadTickets(which);
         if (setRef.current !== which) return;
         const { ticketId: tid, questionId: qid } = selectionRef.current;
         const t = list.find((x) => x.id === tid) || list.find((x) => x.questions.length) || list[0];
         const q = t?.questions.find((x) => x.id === qid) || t?.questions[0];
         setTickets(list);
         setCounts((c) => ({ ...c, [which]: list.length }));
         setTicketId(t?.id ?? null);
         setQuestionId(q?.id ?? null);
      } catch (e) {
         setNotice({ tone: "error", text: e?.message || "Nu am putut reîncărca biletele." });
      }
   }, []);

   useEffect(() => {
      getQuestionCategoriesWithCount()
         .then((rows) => setCategories(Array.isArray(rows) ? rows : rows?.data || []))
         .catch(() => setCategories([]));
   }, []);

   const ticket = tickets.find((t) => t.id === ticketId) || null;
   const qIndex = ticket ? Math.max(0, ticket.questions.findIndex((q) => q.id === questionId)) : 0;
   const original = ticket?.questions[qIndex] || null;
   const draft = original ? drafts[original.id] || original : null;
   const dirty = isDirty(draft, original);

   useEffect(() => setImgFailed(false), [draft?.image, original?.id]);

   const searchIndex = useMemo(() => buildSearchIndex(tickets), [tickets]);
   const hits = useMemo(() => (query.trim().length >= 2 ? search(searchIndex, query) : []), [searchIndex, query]);

   const openQuestion = useCallback((tid, qid) => {
      setTicketId(tid);
      setQuestionId(qid);
      setNotice(null);
   }, []);

   // ── editarea (ciorna întrebării curente) ──
   const edit = (patch) => {
      if (!original) return;
      setDrafts((d) => ({ ...d, [original.id]: { ...(d[original.id] || original), ...patch } }));
      setNotice(null);
   };
   const textKey = `text${cap(lang)}`;
   const hintKey = `hint${cap(lang)}`;
   const editAnswer = (i, value) => edit({ answers: draft.answers.map((a, k) => (k === i ? { ...a, [lang]: value } : a)) });
   const removeAnswer = (i) => {
      const answers = draft.answers.filter((_, k) => k !== i);
      const correct = draft.correct === i ? -1 : draft.correct > i ? draft.correct - 1 : draft.correct;
      edit({ answers, correct });
   };
   const addAnswer = () => edit({ answers: [...draft.answers, { ro: "", ru: "" }] });

   const reset = () => {
      setDrafts((d) => {
         const next = { ...d };
         delete next[original.id];
         return next;
      });
      setNotice(null);
   };

   const save = async () => {
      setSaving(true);
      setNotice(null);
      try {
         const saved = await saveQuestion(draft, original, set);
         setTickets((list) =>
            list.map((t) => (t.id !== ticket.id ? t : { ...t, questions: t.questions.map((q) => (q.id === saved.id ? saved : q)) })),
         );
         setDrafts((d) => {
            const next = { ...d };
            delete next[saved.id];
            return next;
         });
         setNotice({ tone: "success", text: "Salvat." });
      } catch (e) {
         setNotice({ tone: "error", text: e?.message || "Nu am putut salva." });
      } finally {
         setSaving(false);
      }
   };

   const go = (delta) => {
      if (!ticket) return;
      const next = ticket.questions[qIndex + delta];
      if (next) return openQuestion(ticket.id, next.id);
      // la capătul biletului: biletul vecin
      const ti = tickets.findIndex((t) => t.id === ticket.id) + Math.sign(delta);
      const t = tickets[ti];
      if (t && t.questions.length) openQuestion(t.id, delta > 0 ? t.questions[0].id : t.questions[t.questions.length - 1].id);
   };

   // ── uneltele ──
   const runVerify = () => {
      const rows = [];
      tickets.forEach((t) =>
         t.questions.forEach((q) => {
            const issues = verifyQuestion(drafts[q.id] || q);
            if (issues.length) rows.push({ ticket: t, question: q, issues });
         }),
      );
      openSubPopup("questionsVerify", { component: VerifyPopup, setLabel: SETS[set].label, rows, onOpen: openQuestion });
   };

   const TOOLS = [
      { value: "verify", label: "Verifică biletele" },
      { value: "export", label: "Exportă JSON" },
      { value: "categories", label: "Categorii" },
      ...(set === "EXAM"
         ? [
              { value: "import-exam", label: "Import bilete corectate" },
              { value: "import-new", label: "Bilet nou din JSON" },
           ]
         : [{ value: "import-theory", label: "Import teorie: JSON, patch RU" }]),
   ];
   const onTool = async (value) => {
      if (value === "verify") runVerify();
      else if (value === "categories") openPopup("questionCategories");
      else if (value === "import-exam")
         openSubPopup("questionsImportExam", {
            component: ExamImportPopup,
            currentTicketId: ticket?.id ?? null,
            currentTicketLabel: ticket ? `Bilet ${ticket.label} (#${ticket.id})` : "",
            onDone: () => reload("EXAM"),
         });
      else if (value === "import-new")
         openSubPopup("questionsImportNew", { component: ExamNewTicketPopup, onDone: () => reload("EXAM") });
      else if (value === "import-theory")
         openSubPopup("questionsImportTheory", { component: TheoryImportPopup, onDone: () => reload("PRACTICE") });
      else if (value === "export") {
         setNotice({ tone: "loading", text: "Se pregătește exportul…" });
         try {
            await exportSet(set);
            setNotice({ tone: "success", text: "Exportul s-a descărcat." });
         } catch (e) {
            setNotice({ tone: "error", text: e?.message || "Exportul a eșuat." });
         }
      }
   };

   const setOptions = Object.values(SETS).map((s) => ({
      value: s.key,
      label: counts[s.key] != null ? `${s.label} · ${counts[s.key]}` : s.label,
   }));
   const ticketOptions = tickets.map((t) => ({ value: t.id, label: `Bilet ${t.label} · ${t.questions.length}` }));
   const categoryOptions = [
      ...(draft?.categoryId ? [] : [{ value: "", label: "Fără categorie" }]),
      ...categories.map((c) => ({ value: String(c.id), label: c.nameRo || c.nameRu || `#${c.id}` })),
   ];
   const issues = draft ? verifyQuestion(draft) : [];
   const boardCorners = joinedCorners((ticket?.questions || []).map(() => 1), boardCols);
   const img = draft ? imageUrl(draft.image, set) : null;

   return (
      <div className="questionsPage">
         {/* rândul de sus, ca pe Acasă și Personal (scss/_pageToolbar.scss) */}
         <div className="pageToolbar">
            <DropdownUI
               size="lg"
               surface="page"
               value={set}
               onChange={(v) => {
                  setSet(v);
                  setQuery("");
               }}
               options={setOptions}
               aria-label="Setul de bilete"
               className="pageToolbar__menu"
            />
            <SearchBarUI
               surface="page"
               value={query}
               onChange={(e) => setQuery(e.target.value)}
               onClear={() => setQuery("")}
               placeholder="Caută întrebare, bilet (B12, P3) sau răspuns…"
               aria-label="Caută întrebare"
               className="pageToolbar__search"
            />
            <div className="pageToolbar__controls">
               <DropdownUI
                  size="lg"
                  surface="page"
                  value={lang}
                  onChange={(v) => {
                     setLang(v);
                     try {
                        localStorage.setItem("exam.lang", v);
                     } catch {}
                  }}
                  options={LANGS}
                  label={lang.toUpperCase()}
                  aria-label="Limba"
               />
               <DropdownUI
                  tone="primary"
                  size="lg"
                  align="end"
                  icon={addIcon}
                  label="Unelte"
                  options={TOOLS}
                  onChange={onTool}
                  aria-label="Unelte"
               />
            </div>
         </div>

         {/* rezultatele căutării: un clic deschide întrebarea */}
         {query.trim().length >= 2 && (
            <CardUI level="screen" titleSize="sm" title={`Rezultate · ${hits.length}${hits.length >= 60 ? "+" : ""}`}>
               {hits.length ? (
                  <ListUI columns={2}>
                     {hits.map((h) => {
                        const t = tickets.find((x) => x.id === h.ticketId);
                        const q = t?.questions.find((x) => x.id === h.questionId);
                        if (!t || !q) return null;
                        const text = q[textKey] || q.textRo || q.textRu;
                        return (
                           <ListUI.Item
                              key={q.id}
                              title={`${t.label} · întrebarea ${q.order}`}
                              meta={`#${q.id}`}
                              detail={<SearchBarUI.Highlight text={text} query={query.trim().split(/\s+/)[0]} />}
                              onClick={() => {
                                 openQuestion(t.id, q.id);
                                 setQuery("");
                              }}
                           />
                        );
                     })}
                  </ListUI>
               ) : (
                  <ListUI>
                     <ListUI.Empty>Nicio întrebare nu se potrivește.</ListUI.Empty>
                  </ListUI>
               )}
            </CardUI>
         )}

         <section className="questionsPage__exam">
            {loading && <StatusUI look="block" tone="loading">Se încarcă biletele…</StatusUI>}
            {!loading && error && (
               <StatusUI look="block" tone="error" action={<ButtonUI onClick={() => load(set)}>Reîncearcă</ButtonUI>}>
                  {error}
               </StatusUI>
            )}
            {!loading && !error && !ticket && <StatusUI look="block" tone="empty">Nu sunt bilete în acest set.</StatusUI>}

            {!loading && !error && ticket && (
               <>
                  {/* antetul, ca la elev: biletul, întrebarea i/n, numerele întrebărilor */}
                  <div className="questionsPage__head">
                     <DropdownUI
                        value={ticket.id}
                        onChange={(v) => {
                           const t = tickets.find((x) => String(x.id) === String(v));
                           if (t) openQuestion(t.id, t.questions[0]?.id ?? null);
                        }}
                        options={ticketOptions}
                        label={`Bilet ${ticket.label}`}
                        aria-label="Biletul"
                     />
                     {original && (
                        <span className="questionsPage__index">
                           Întrebarea {qIndex + 1}/{ticket.questions.length}
                        </span>
                     )}
                     {original && <TagUI tone="page">#{original.id}</TagUI>}
                     {dirty && <TagUI tone="warning">Nesalvat</TagUI>}
                  </div>

                  <div ref={boardRef} className="questionsPage__board" role="tablist" aria-label="Întrebările biletului">
                     {ticket.questions.map((q, i) => {
                        const d = drafts[q.id];
                        const bad = verifyQuestion(d || q).length > 0;
                        return (
                           <button
                              key={q.id}
                              type="button"
                              role="tab"
                              aria-selected={q.id === original?.id}
                              title={bad ? "Are probleme" : undefined}
                              className={cx(
                                 "questionsPage__dot",
                                 q.id === original?.id && "is-current",
                                 d && isDirty(d, q) && "is-dirty",
                                 bad && "is-bad",
                                 cornerClass(boardCorners[i]),
                              )}
                              onClick={() => openQuestion(ticket.id, q.id)}
                           >
                              {i + 1}
                           </button>
                        );
                     })}
                  </div>

                  {!original && <StatusUI look="block" tone="empty">Biletul nu are întrebări.</StatusUI>}

                  {draft && (
                     <div className="questionsPage__question">
                        <AutoText
                           className="questionsPage__qtext"
                           value={draft[textKey]}
                           onChange={(v) => edit({ [textKey]: v })}
                           placeholder={lang === "ru" ? "Текст вопроса" : "Textul întrebării"}
                           ariaLabel="Textul întrebării"
                        />

                        <div className="questionsPage__row">
                           {/* imaginea, ca la elev; dedesubt linkul ei */}
                           <div className="questionsPage__media">
                              <div className="questionsPage__image">
                                 {img ? (
                                    <img
                                       src={imgFailed ? NO_IMAGE_URL : img}
                                       alt=""
                                       onError={() => setImgFailed(true)}
                                    />
                                 ) : (
                                    <span className="questionsPage__noImage">Fără imagine</span>
                                 )}
                              </div>
                              <InputUI
                                 label="Link imagine"
                                 value={draft.image}
                                 onChange={(e) => edit({ image: e.target.value })}
                                 placeholder="/images/B12/Image3.jpeg"
                              />
                           </div>

                           {/* răspunsurile: cercul alege răspunsul corect (verde, ca la elev) */}
                           <div className="questionsPage__answersCol">
                              <div className="questionsPage__answers">
                                 {draft.answers.map((a, i) => (
                                    <div key={i} className={cx("questionsPage__answer", draft.correct === i && "is-correct")}>
                                       <button
                                          type="button"
                                          className="questionsPage__mark"
                                          aria-pressed={draft.correct === i}
                                          aria-label={`Răspunsul ${i + 1} e cel corect`}
                                          title="Răspunsul corect"
                                          onClick={() => edit({ correct: i })}
                                       >
                                          {i + 1}
                                       </button>
                                       <AutoText
                                          className="questionsPage__answerText"
                                          value={a[lang]}
                                          onChange={(v) => editAnswer(i, v)}
                                          placeholder={lang === "ru" ? "Ответ" : "Răspuns"}
                                          ariaLabel={`Răspunsul ${i + 1}`}
                                       />
                                       {draft.answers.length > MIN_ANSWERS && (
                                          <ButtonUI
                                             variant="ghost"
                                             size="sm"
                                             icon="close"
                                             aria-label={`Șterge răspunsul ${i + 1}`}
                                             title="Șterge răspunsul"
                                             onClick={() => removeAnswer(i)}
                                          />
                                       )}
                                    </div>
                                 ))}
                              </div>
                              {draft.answers.length < MAX_ANSWERS && (
                                 <ButtonUI variant="subtle" icon="add" onClick={addAnswer}>
                                    Adaugă răspuns
                                 </ButtonUI>
                              )}

                              <InputUI.Group columns={1}>
                                 <InputUI
                                    as="select"
                                    label="Categorie"
                                    value={draft.categoryId ? String(draft.categoryId) : ""}
                                    onChange={(e) => edit({ categoryId: e.target.value ? Number(e.target.value) : null })}
                                    options={categoryOptions}
                                    hint={
                                       original.categoryId
                                          ? "Serverul nu scoate o întrebare dintr-o categorie: o poți muta în alta."
                                          : set === "EXAM"
                                            ? "Categoriile contează pentru elevi doar la teorie."
                                            : undefined
                                    }
                                 />
                                 {SETS[set].hint && (
                                    <InputUI
                                       as="textarea"
                                       label={lang === "ru" ? "Пояснение (RU)" : "Explicație (RO)"}
                                       rows={2}
                                       value={draft[hintKey]}
                                       onChange={(e) => edit({ [hintKey]: e.target.value })}
                                    />
                                 )}
                              </InputUI.Group>
                           </div>
                        </div>

                        {issues.length > 0 && <StatusUI tone="warning">{issues.join(" · ")}</StatusUI>}
                        {notice && <StatusUI tone={notice.tone}>{notice.text}</StatusUI>}

                        <ButtonUI.Row
                           align="split"
                           leading={
                              <ButtonUI onClick={() => go(-1)} disabled={qIndex === 0 && tickets[0]?.id === ticket.id}>
                                 Înapoi
                              </ButtonUI>
                           }
                        >
                           <ButtonUI variant="subtle" onClick={reset} disabled={!dirty || saving}>
                              Resetează
                           </ButtonUI>
                           <ButtonUI variant="primary" onClick={save} loading={saving} disabled={!dirty || saving}>
                              Salvează
                           </ButtonUI>
                           <ButtonUI onClick={() => go(1)}>Următorul</ButtonUI>
                        </ButtonUI.Row>
                     </div>
                  )}
               </>
            )}
         </section>
      </div>
   );
}
